import React, {
  Children,
  useRef,
  useState,
  useMemo,
  useCallback,
  useLayoutEffect,
  useEffect,
  forwardRef,
  type ReactNode,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import { CarouselProps } from "./types";
import { Button } from "../Button/Button";
import { SkipLink } from "../SkipLink/SkipLink";
import { GradientOverlay } from "./components/GradientOverlay";

import cx from "components/src/utils/cx";
import { randomIdString } from "shared/src/utils/randomIdString";
import { useConfig } from "./js/carousel.config";

import omit from "lodash-es/omit";

// CSS
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import styles from "./css/Carousel.module.scss";
import type { DivProps } from "react-html-props";

const skipLinkDefault = { anchorTag: "", label: "Carousel" };
const breakpointValues = {
  mobile: "280",
  tablet: "768",
};

// const htmlElementTypesArray = ["div", "span", "button", "h1", "h2", "h3", "h4", "h5", "h6", "p", "section", "article", "li"];

export const Carousel = ({
  overlay = true,
  showPagination = true,
  showControls = "hover",
  controlSize = "lg",
  controlButtonType = "tertiary",
  controlButtonsHideAtEdge = false,
  skipLink = skipLinkDefault,
  showFade = true,
  forceFade,
  showFadeLeft = true,
  showFadeRight = true,
  fadeType = "light",
  fadeColor, 
  fadeTopOffset,
  slideTo = 0,
  itemsDescription = "",
  itemSpacing = "4",
  centerSlides,
  initialIndex = 0,
  slideWrapperClasses,
  cssMode = false,
  disableMouseWheel = false,
  tabbable = true,
  topPadding,
  bottomPadding,
  leftPadding,
  rightPadding,
  id,
  disabled = false,
  role = "group",
  forcePagination,
  disableSlideTo = false,
  disableFreeMode,
  carouselScrollDebounceSpeed,
  carouselScrollDebounceOptions,
  onItemChange,
  onCarouselScroll,
  onCarouselTouchStart,
  onAfterInit,
  onBeforeResize,
  onResize,
  handleItemImpression,
  children,
  style,
  as,
  refObj,
  ...rest
}: CarouselProps) => {
  const randStr = useRef<string>(randomIdString());
  const generatedId = `carousel-${randStr.current}`;
  const navBtns = useMemo(
    () => ({
      nextElId: `cui__carousel-next-button-${randStr.current}`,
      prevElId: `cui__carousel-prev-button-${randStr.current}`,
    }),
    []
  );
  const paginationClass = `cui__carousel-pagination-${randStr.current}`;
  const carouselWrapper = useRef<HTMLDivElement | null>(null);
  const isFirstRender = useRef(true);

  const [_enabled, setEnabled] = useState<boolean>(!disabled);
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  const { config, breakpoint, isEnd, isBeginning, isLocked, swiperInstance } =
    useConfig({
      navBtns,
      paginationClass,
      cssMode,
      showPagination,
      forcePagination,
      itemSpacing,
      centerSlides,
      randStr,
      initialIndex,
      _enabled,
      disableMouseWheel,
      disableFreeMode,
      carouselScrollDebounceSpeed,
      leftPadding,
      rightPadding,
      onItemChange,
      handleItemImpression,
      onCarouselScroll,
      onCarouselTouchStart,
      onAfterInit,
      onBeforeResize,
      onResize
    });

  const isOverflowing =
    !!swiperInstance.current &&
    !(swiperInstance.current.isBeginning && swiperInstance.current.isEnd);

  const _showFade = {
    showFade: showFade && isOverflowing,
    showFadeLeft: showFade && showFadeLeft && isOverflowing,
    showFadeRight: showFade && showFadeRight && isOverflowing,
  };

  useLayoutEffect(() => {
    if (swiperInstance.current && window !== undefined) {
      let carousel = swiperInstance.current;
      if (
        typeof initialIndex === "number" &&
        carousel &&
        isFirstRender.current
      ) {
        !disableSlideTo && carousel.slideTo(initialIndex, 200);
        isFirstRender.current = false;
      } else if (typeof slideTo === "number" && carousel) {
        !disableSlideTo && carousel.slideTo(slideTo, 200);
      }
    }
  }, [showPagination, slideTo, initialIndex]);

  useLayoutEffect(() => {
    if (swiperInstance.current) {
      let carousel = swiperInstance.current;
      if (hasFocus) {
        carousel.keyboard.enable();
      } else {
        carousel.keyboard.disable();
      }
    }
  }, [showPagination, hasFocus]);

  useEffect(() => {
    setEnabled(!disabled && isOverflowing);
    if (swiperInstance.current) {
      if (!disabled && isOverflowing && !swiperInstance.current.enabled)
        swiperInstance.current.enable();
      if (disabled || (!isOverflowing && swiperInstance.current.enabled))
        swiperInstance.current.disable();
    }
  }, [disabled, isOverflowing]);

  useEffect(()=>{
    if (swiperInstance.current) {
      swiperInstance.current.disable();
      swiperInstance.current.enable();
    }
  },[controlSize]);

  /**
   * Handlers
   */

  const focusHandler = useCallback((e) => {
    setHasFocus(true);
  }, []);
  const blurHandler = useCallback((e) => {
    setHasFocus(false);
  }, []);

  const escapeKeyHandler = useCallback(
    (e) => {
      if (e.key === "Escape") {
        const el = carouselWrapper.current?.firstChild as HTMLElement;
        el.focus();
      }
    },
    [carouselWrapper.current]
  );

  const _styles = useMemo(() => {
    let styles = { ...style };
    if (topPadding) {
      styles["--carousel-top-padding"] = topPadding;
    }
    if (bottomPadding) {
      styles["--carousel-bottom-padding"] = bottomPadding;
    }
    return styles;
  }, [topPadding, bottomPadding]);

  const slides = useMemo(() => {
    return Children.map(children, (child, i) => {
      return (
        <SwiperSlide
          tabIndex={-1}
          virtualIndex={i}
          onKeyUp={escapeKeyHandler}
          role="group"
        >
          {child}
        </SwiperSlide>
      );
    });
  }, [children]);

  const hasConfiguration = !!Object.entries(config).length;
  
  return (
    <CarouselWrapper
      navigation={navBtns}
      overlay={overlay}
      breakpoint={breakpoint}
      showPagination={showPagination}
      showControls={showControls}
      controlSize={controlSize}
      controlButtonType={controlButtonType}
      controlButtonsHideAtEdge={controlButtonsHideAtEdge}
      isEnd={isEnd}
      isBeginning={isBeginning}
      skipLink={skipLink}
      onFocus={focusHandler}
      onBlur={blurHandler}
      itemsDescription={itemsDescription}
      isLocked={isLocked}
      tabbable={tabbable}
      forcePagination={forcePagination}
      slideWrapperClasses={slideWrapperClasses}
      paginationClass={paginationClass}
      role={role}
      ref={carouselWrapper}
      id={id ? id : generatedId}
      style={_styles}
      as={as}
      aria-label={`${
          itemsDescription ? `Carousel for ${itemsDescription}` : `${generatedId}`
        }`}
      {...rest}
    >
      {hasConfiguration ? (
        <Swiper
          {...config}
          className={`cui__carousel swiper-slide cui__carousel_${randStr.current} focus:cui-outline-none`}
          ref={refObj}
        >
          {slides}
          {showFade ? (
            <>
              <GradientOverlay
                slot="container-start"
                className={cx(
                  {
                    "cui-opacity-0": isBeginning || !_showFade.showFadeLeft,
                    "cui-opacity-100": !isBeginning && _showFade.showFadeLeft,
                  },
                  "cui-left-0"
                )}
                type={fadeType}
                topOffset={fadeTopOffset}
                forceFade={forceFade}
                fadeColor={fadeColor}
                visible={_showFade.showFadeLeft && !isBeginning}
              />
              <GradientOverlay
                slot="container-end"
                className={cx(
                  {
                    "cui-opacity-0": isEnd || !_showFade.showFadeRight,
                    "cui-opacity-100": !isEnd && _showFade.showFadeRight,
                  },
                  "cui-right-0"
                )}
                type={fadeType}
                forceFade={forceFade}
                fadeColor={fadeColor}
                topOffset={fadeTopOffset}
                visible={_showFade.showFadeRight && !isEnd}
                flip
              />
            </>
          ) : null}
        </Swiper>
      ) : null}
    </CarouselWrapper>
  );
};

const CarouselNode = forwardRef<
  HTMLDivElement,
  { as: "div" | "nav" | undefined; children: ReactNode } & DivProps
>(({ as = "div", children, ...rest }, ref) => {
  if (as === "nav") {
    const _rest = omit(rest, ["role", "spaceBetween"]);
    return (
      <nav {..._rest} ref={ref}>
        {children}
      </nav>
    );
  }
  return (
    <div {...rest} ref={ref}>
      {children}
    </div>
  );
});

const innerJustifyMap = {
  start: "cui-justify-start",
  center: "cui-justify-center",
  end: "cui-justify-end",
};

export const CarouselWrapper = forwardRef(
  (
    {
      navigation: { nextElId, prevElId },
      overlay,
      breakpoint,
      showPagination,
      showControls,
      controlSize,
      controlButtonType,
      controlButtonsHideAtEdge,
      skipLink,
      isEnd,
      isBeginning,
      itemsDescription = "",
      isLocked,
      onFocus,
      onBlur,
      id,
      role = "list",
      className = "",
      forcePagination,
      paginationClass,
      slideWrapperClasses = "",
      tabbable = true,
      innerJustify = "start",
      as,
      children,
      ...rest
    }: Partial<CarouselProps> & {
      breakpoint: "280" | "768" | undefined;
      isEnd: boolean | undefined;
      isBeginning: boolean | undefined;
      isLocked: boolean;
      onFocus: (e: any) => void;
      onBlur: (e: any) => void;
      className?: string;
      paginationClass?: string;
      navigation: {
        nextElId: string;
        prevElId: string;
      };
      as?: "div" | "nav";
    },
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const isMobile = breakpoint === breakpointValues.mobile;
    const noOverlay = !overlay && !isMobile && !isLocked && showControls !== "none";
    const hasOverlay = overlay || isMobile || showControls === "none";
    const showControlFade =
      showControls !== "none" &&
      !isLocked &&
      (showControls === "always" || noOverlay);
    const _showPagination = showPagination || forcePagination;
    const navBtnClasses = cx(
      {
        "cui-absolute": hasOverlay,
        "cui-invisible": isMobile || showControls === "none",
        "cui-relative": noOverlay,
        "cui-opacity-0 group-hover:cui-opacity-100":
          showControls === "hover" && hasOverlay && !isLocked,
        "cui-opacity-0": (showControls === "none" && hasOverlay) || isLocked,
      },
      "cui-z-10 cui-transition-opacity cui-duration-200"
    );
    const hidePagination = (!forcePagination && isMobile) || !_showPagination;
    const _innerJustify =
      innerJustify in innerJustifyMap
        ? innerJustifyMap[innerJustify]
        : innerJustifyMap["start"];

    return (
      <CarouselNode
        className={`${styles["cui__carousel"]} cui-w-full cui-relative cui-flex cui-flex-col cui-group focus-visible:cui-outline-1 focus-visible:cui-outline-fg-focus focus-visible:cui-outline-offset-2 ${className}`}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={ref}
        tabIndex={tabbable ? 0 : -1}
        id={id}
        role="region"
        aria-label={`Carousel ${
          itemsDescription ? `for ${itemsDescription}` : ""
        }`}
        as={as}
        {...rest}
      >
        {skipLink ? <SkipLink {...skipLink} /> : null}
        <div
          className={`cui-flex cui-items-center cui-relative ${_innerJustify}`}
        >
          <div
            className={cx(
              {
                "cui-ml-2": hasOverlay,
                "cui-mr-sm": noOverlay && !isLocked,
                [styles["cui__carousel_controls_fadein"]]: showControlFade,
                "cui-pointer-events-none":
                  isBeginning || (hasOverlay && showControls === "none"),
                "cui-invisible": !!isBeginning && !!controlButtonsHideAtEdge,
              },
              `cui-left-0 ${navBtnClasses} cui-transition-all cui-duration-200`
            )}
            tabIndex={-1}
          >
            <Button
              id={prevElId}
              size={controlSize}
              type={controlButtonType}
              leftIcon="ui_chevron_left"
              disabled={isBeginning}
              tabIndex={-1}
              aria-label="Previous Item Button"
            />
          </div>
          <div
            className={`cui-relative cui-overflow-hidden ${slideWrapperClasses}`}
            tabIndex={-1}
          >
            {children}
          </div>
          <div
            className={cx(
              {
                "cui-mr-2": hasOverlay,
                "cui-ml-sm": noOverlay && !isLocked,
                [styles["cui__carousel_controls_fadein"]]: showControlFade,
                "cui-pointer-events-none":
                  isEnd || (hasOverlay && showControls === "none"),
                "cui-invisible": !!isEnd && !!controlButtonsHideAtEdge,
              },
              `cui-right-0 ${navBtnClasses} cui-transition-all cui-duration-200`
            )}
            tabIndex={-1}
          >
            <Button
              id={nextElId}
              size={controlSize}
              type={controlButtonType}
              disabled={isEnd}
              leftIcon="ui_chevron_right"
              tabIndex={-1}
              aria-label="Next Item Button"
            />
          </div>
        </div>
        <div
          className={cx(
            {
              "cui-invisible cui-hidden": hidePagination,
              // (!forcePagination && breakpoint === breakpointValues.mobile ) || !_showPagination,
            },
            "cui__carousel-pagination-wrapper cui-flex cui-justify-center cui-mt-xs cui-pl-24"
          )}
          tabIndex={_showPagination ? 0 : -1}
        >
          <div
            className={`cui__carousel-pagination ${paginationClass}`}
            tabIndex={_showPagination ? 0 : -1}
            aria-hidden={_showPagination ? "false" : "true"}
          />
        </div>
      </CarouselNode>
    );
  }
);
